import { useState } from 'react';
import { useRouter } from 'next/router';

import { Box, Heading, Paragraph, Popover, TextButton } from '@zillow/constellation-10';

import { useGetListingDetails, useGetListingRecommendations } from '../../../../../hooks/queries/properties';
import { useAiGeneratedFields } from '../../../../../hooks/queries/useAiGeneratedFields';
import numberUtils from '../../../../../utils/numberUtils';
import propertyUtils from '../../../../../utils/propertyUtils';
import typeCheckUtils from '../../../../../utils/typeCheckUtils';
import { getIsInputFormDisabled } from '../../../../MFAccordion/utils';

import { REVIEW_PAGE_SECTIONS } from '../../../../../constants/ListingDetailConstants';
import { RECS } from '../../../../../constants/RecommendationsConstants';
import { SF_MODAL_COMPONENTS } from '../constants';

import CloneThisListingConversionModal from '../../../../modals/CloneThisListingConversionModal';
import RecommendationModal from '../../../../modals/RecommendationModal';
import AccordionIconError from '../components/AccordionIconError';
import AIAssistedFieldIcon from '../components/AIAssistedFieldIcon';
import ContentSectionEditButton from '../components/ContentSectionEditButton';
import FieldValueDisplay from '../components/FieldValueDisplay';

import { iconPlaceholderStyles, reviewSectionStyles, reviewSectionTitleStyles, reviewTitleRowStyles } from '../styles';

import { useAuth } from '../../../../AuthProvider';
import RecommendationIconBulb from '../../../../PostingPath/RecommendationIconBulb';

const PropertyInformationContent = ({
  onEditClick,
}: {
  onEditClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}) => {
  const router = useRouter();
  const { user } = useAuth();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);
  const { recommendationsData, recommendationsBySection } = useGetListingRecommendations({
    listingAlias,
    isMultifamily: false,
  });
  const { hasAIDescription } = useAiGeneratedFields();

  const floorplans = listingDetails?.floorPlans || [];
  const isFeed = listingDetails?.isFeed || false;

  const [displayRecsModal, setDisplayRecsModal] = useState('');
  const [showCloneModal, setShowCloneModal] = useState(false);

  const customNoneFieldValueText = isFeed ? 'None' : 'Action needed';

  const isRoomForRent = propertyUtils.isRoomForRent({ listing: listingDetails });

  const propertyDescriptionHeading = isRoomForRent ? 'Room and shared space description' : 'Property description';

  const floorplan = floorplans[0];
  const isDisabled = getIsInputFormDisabled(listingDetails, user?.features?.canUpdateListing ?? false);

  const squareFootageRec = recommendationsBySection[REVIEW_PAGE_SECTIONS.PROPERTY_INFORMATION]?.[RECS.SQUARE_FOOTAGE];
  const hideStreetAddressRec =
    recommendationsBySection[REVIEW_PAGE_SECTIONS.PROPERTY_INFORMATION]?.[RECS.HIDE_STREET_ADDRESS];
  const descriptionRec = recommendationsBySection[REVIEW_PAGE_SECTIONS.PROPERTY_INFORMATION]?.[RECS.DESCRIPTION];
  const recommendationTypes = new Set(recommendationsData?.recommendations?.map((r) => r.type) ?? []);

  const { formattedCompleteAddress, formattedAddressWithoutUnit } = propertyUtils.formatAddress({
    street: listingDetails?.street,
    city: listingDetails?.city,
    state: listingDetails?.state,
    zip: listingDetails?.zip,
    unit: listingDetails?.unit,
  });

  const getSquareFootageDisplay = () => {
    const squareFeet = typeCheckUtils.Number(floorplan?.squareFeet);
    if (squareFeet == null || squareFeet < 1) {
      return null;
    }
    return `${numberUtils.prettyPrintNumber(squareFeet)} sqft`;
  };

  const getRoomSquareFootageDisplay = () => {
    const roomSquareFeet = typeCheckUtils.Number(floorplan?.roomForRent?.roomSquareFeet);
    if (roomSquareFeet == null || roomSquareFeet < 1) {
      return null;
    }
    return `${numberUtils.prettyPrintNumber(roomSquareFeet)} sqft`;
  };

  return (
    <div data-testid="PropertyInformationContent" data-group-title="PropertyInformation">
      <Box css={reviewSectionStyles} data-testid="Address">
        <Box css={reviewTitleRowStyles}>
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <Box css={iconPlaceholderStyles} />
            <Heading level={4} css={{ textStyle: 'body-bold' }}>
              Address
            </Heading>
          </Box>

          <ContentSectionEditButton
            fieldLabel={SF_MODAL_COMPONENTS.AddressFormField.title}
            useLockIcon={listingDetails?.restrictions?.isUpdateUnitNumberRestricted}
            onEditClick={onEditClick}
            disabledMessage={
              listingDetails?.restrictions?.isCloneListingRestricted ? (
                'Once a property is created, its unique property address can’t be edited. Instead, create a new property with the correct address.'
              ) : (
                <Box
                  data-testid="CloneThisListing-Message-Container"
                  css={{ display: 'flex', flexDirection: 'column', gap: '400' }}
                >
                  <Paragraph>
                    Once a property is created, its unique property address can&apos;t be edited. Instead, create a new
                    property with the correct address.
                  </Paragraph>

                  <Paragraph>
                    If you would like to post another unit with similar listing details, you can make a copy.
                  </Paragraph>

                  <Box>
                    <Popover.Close>
                      <TextButton onClick={() => setShowCloneModal(true)}>Clone this listing</TextButton>
                    </Popover.Close>
                  </Box>
                </Box>
              )
            }
            popoverHeading="Need to edit your property address?"
            value={SF_MODAL_COMPONENTS.AddressFormField.componentKey}
          />
        </Box>
        <FieldValueDisplay value={formattedCompleteAddress} />
      </Box>

      <Box css={reviewSectionStyles} data-testid="HidePropertyAddress">
        <Box css={reviewTitleRowStyles}>
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <Box css={iconPlaceholderStyles}>
              {hideStreetAddressRec && (
                <RecommendationIconBulb
                  label={SF_MODAL_COMPONENTS.HideAddressFormField.title}
                  onClick={() => {
                    setDisplayRecsModal(RECS.HIDE_STREET_ADDRESS);
                  }}
                />
              )}
            </Box>
            <Box css={reviewSectionTitleStyles}>
              <Heading level={4} css={{ textStyle: 'body-bold' }}>
                Hide property address
              </Heading>
              {recommendationTypes.has(RECS.HIDE_STREET_ADDRESS) && (
                <TextButton
                  aria-label={`Learn more about ${SF_MODAL_COMPONENTS.HideAddressFormField.title}`}
                  textStyle="body"
                  onClick={() => {
                    setDisplayRecsModal(RECS.HIDE_STREET_ADDRESS);
                  }}
                >
                  Learn more
                </TextButton>
              )}
            </Box>
          </Box>

          <ContentSectionEditButton
            fieldLabel={SF_MODAL_COMPONENTS.HideAddressFormField.title}
            value={SF_MODAL_COMPONENTS.HideAddressFormField.componentKey}
            onEditClick={onEditClick}
          />
        </Box>
        <FieldValueDisplay value={listingDetails?.hideStreetAddress ? 'Yes' : 'No'} />
      </Box>

      <Box css={reviewSectionStyles} data-testid="SquareFootage">
        <Box css={reviewTitleRowStyles}>
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <Box css={iconPlaceholderStyles}>
              {squareFootageRec && (
                <RecommendationIconBulb
                  label={SF_MODAL_COMPONENTS.SquareFootageFormField.title}
                  onClick={() => {
                    setDisplayRecsModal(RECS.SQUARE_FOOTAGE);
                  }}
                />
              )}
            </Box>
            <Box css={reviewSectionTitleStyles}>
              <Heading level={4} css={{ textStyle: 'body-bold' }}>
                Square footage
              </Heading>
              {recommendationTypes.has(RECS.SQUARE_FOOTAGE) && (
                <TextButton
                  aria-label={`Learn more about ${SF_MODAL_COMPONENTS.SquareFootageFormField.title}`}
                  textStyle="body"
                  onClick={() => {
                    setDisplayRecsModal(RECS.SQUARE_FOOTAGE);
                  }}
                >
                  Learn more
                </TextButton>
              )}
            </Box>
          </Box>

          <ContentSectionEditButton
            fieldLabel={SF_MODAL_COMPONENTS.SquareFootageFormField.title}
            value={SF_MODAL_COMPONENTS.SquareFootageFormField.componentKey}
            onEditClick={onEditClick}
          />
        </Box>
        <FieldValueDisplay value={getSquareFootageDisplay() ?? ''} />
      </Box>

      {isRoomForRent && (
        <Box css={reviewSectionStyles} data-testid="RoomSquareFootage">
          <Box css={reviewTitleRowStyles}>
            <Box css={{ display: 'flex', alignItems: 'center' }}>
              <Box css={iconPlaceholderStyles}>
                {squareFootageRec && (
                  <RecommendationIconBulb
                    label={SF_MODAL_COMPONENTS.RoomSquareFootageFormField.title}
                    onClick={() => {
                      setDisplayRecsModal(RECS.SQUARE_FOOTAGE);
                    }}
                  />
                )}
              </Box>
              <Heading level={4} css={{ textStyle: 'body-bold' }}>
                Room square footage
              </Heading>
            </Box>

            <ContentSectionEditButton
              fieldLabel={SF_MODAL_COMPONENTS.RoomSquareFootageFormField.title}
              value={SF_MODAL_COMPONENTS.RoomSquareFootageFormField.componentKey}
              onEditClick={onEditClick}
            />
          </Box>
          <FieldValueDisplay value={getRoomSquareFootageDisplay() ?? ''} />
        </Box>
      )}

      <Box css={reviewSectionStyles} data-testid="Beds">
        <Box css={reviewTitleRowStyles}>
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <Box css={iconPlaceholderStyles}>
              {floorplan?.numBedrooms == undefined && !isDisabled && (
                <AccordionIconError title="Bedrooms incomplete error" marginRight="xs" />
              )}
            </Box>
            <Heading level={4} css={{ textStyle: 'body-bold' }}>
              Bedrooms
            </Heading>
          </Box>

          <ContentSectionEditButton
            fieldLabel={SF_MODAL_COMPONENTS.BedroomsFormField.title}
            value={SF_MODAL_COMPONENTS.BedroomsFormField.componentKey}
            onEditClick={onEditClick}
          />
        </Box>
        <FieldValueDisplay
          value={String(floorplan?.numBedrooms) === '0' ? 'Studio' : String(floorplan?.numBedrooms || '')}
          customNoneText={customNoneFieldValueText}
        />
      </Box>

      <Box css={reviewSectionStyles} data-testid="Baths">
        <Box css={reviewTitleRowStyles}>
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <Box css={iconPlaceholderStyles}>
              {String(floorplan?.numBathrooms || '') === '' && !isDisabled && (
                <AccordionIconError title="Bathrooms incomplete error" marginRight="xs" />
              )}
            </Box>
            <Heading level={4} css={{ textStyle: 'body-bold' }}>
              Bathrooms
            </Heading>
          </Box>

          <ContentSectionEditButton
            fieldLabel={SF_MODAL_COMPONENTS.BathroomsFormField.title}
            value={SF_MODAL_COMPONENTS.BathroomsFormField.componentKey}
            onEditClick={onEditClick}
          />
        </Box>
        <FieldValueDisplay value={String(floorplan?.numBathrooms || '')} customNoneText={customNoneFieldValueText} />
      </Box>

      <Box css={reviewSectionStyles} data-testid="PropertyType">
        <Box css={reviewTitleRowStyles}>
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <Box css={iconPlaceholderStyles} />
            <Heading level={4} css={{ textStyle: 'body-bold' }}>
              Property type
            </Heading>
          </Box>

          <ContentSectionEditButton
            fieldLabel={SF_MODAL_COMPONENTS.PropertyTypeFormField.title}
            value={SF_MODAL_COMPONENTS.PropertyTypeFormField.componentKey}
            onEditClick={onEditClick}
          />
        </Box>
        <FieldValueDisplay value={listingDetails?.propertyType.display} />
      </Box>

      <Box data-testid="PropertyDescription">
        <Box css={reviewTitleRowStyles}>
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            {isRoomForRent && !listingDetails?.fullDescription ? (
              <Box css={iconPlaceholderStyles}>
                <AccordionIconError title="Description incomplete error" marginRight="xs" />
              </Box>
            ) : (
              <Box css={iconPlaceholderStyles}>
                {descriptionRec ? (
                  <RecommendationIconBulb
                    label={propertyDescriptionHeading}
                    onClick={() => {
                      setDisplayRecsModal(RECS.DESCRIPTION);
                    }}
                  />
                ) : (
                  hasAIDescription && (
                    <AIAssistedFieldIcon title={`${propertyDescriptionHeading} was drafted with AI`} />
                  )
                )}
              </Box>
            )}

            <Box css={reviewSectionTitleStyles}>
              <Heading level={4} css={{ textStyle: 'body-bold' }}>
                {propertyDescriptionHeading}
              </Heading>
              {recommendationTypes.has(isRoomForRent ? RECS.ROOM_DESCRIPTION : RECS.DESCRIPTION) && (
                <TextButton
                  aria-label={`Learn more about ${propertyDescriptionHeading}`}
                  textStyle="body"
                  onClick={() => {
                    setDisplayRecsModal(isRoomForRent ? RECS.ROOM_DESCRIPTION : RECS.DESCRIPTION);
                  }}
                >
                  Learn more
                </TextButton>
              )}
            </Box>
          </Box>

          <ContentSectionEditButton
            fieldLabel={propertyDescriptionHeading}
            value={SF_MODAL_COMPONENTS.PropertyDescriptionFormField.componentKey}
            onEditClick={onEditClick}
          />
        </Box>
        <FieldValueDisplay value={listingDetails?.fullDescription} />
      </Box>
      {showCloneModal && (
        <CloneThisListingConversionModal
          listingAlias={listingAlias}
          unit={listingDetails?.unit}
          formattedAddressWithoutUnit={formattedAddressWithoutUnit}
          onClose={() => setShowCloneModal(false)}
        />
      )}
      {displayRecsModal && (
        <RecommendationModal onClose={() => setDisplayRecsModal('')} recommendationType={displayRecsModal} />
      )}
    </div>
  );
};

export default PropertyInformationContent;
